import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, FormControl, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import moment from '../../moment';
import InfoBar from '../../components/Navigation/InfoBar';
import useApi from '../../hooks/use-api';
import { useToast } from '../../hooks/use-toast';
import Address from '../../components/Form/Address';
import {
	dationInstanceEndpoint,
	deleteCompanyContactPerson,
	EDIT_USER,
	getSendInviteUrl,
	inviteContactPersonToPortal,
} from '../../constants/routes';
import * as ROUTES from '../../constants/routes';
import useMergeState from '../../hooks/use-merge-state';
import FormCol from '../../components/Form/FormCol';
import ConfirmModal from '../../components/Modal/ConfirmModal';
import { parseErrorMessage } from '../../utils/parseErrors';
import SmallFormLabel from '../../components/Form/SmallFormLabel';
import ErrorMessage from '../../components/common/messages/ErrorMessage';
import MultiSelect, { formatForOptions } from '../../components/common/MultiSelect';
import DropdownButton from '../../components/common/DropdownButton';
import { useUser } from '../../hooks/use-user';
import CompanyContactPersonOverview from '../../components/Tables/Users/CompanyContactPersonUser/CompanyContactPersonOverview';
import InviteUserModal from '../../components/Modal/InviteUserModal';
import DeleteUserModal from '../DeleteUserModal';
import { getDisplayName } from '../../utils/person';
import { parseInt } from "lodash/string";
import LinkDrivingSchoolModal from '../../components/Modal/LinkDrivingSchoolModal';

function CompanyDetailPage() {
	const { companyId } = useParams();
	const { t } = useTranslation();
	const toast = useToast();
	const exportTitle = useRef('');
	const { profile } = useUser();
	const navigate = useNavigate();

	const initialState = {
		name: undefined,
		soob: false,
		private: false,
		emailAddress: undefined,
		vatNumber: undefined,
		phone: undefined,
		website: undefined,
		companyRegistrationNumber: undefined,
		department: undefined,
		notes: undefined,
		archived: false,
		sharedCompanies: [],
		lastExportDate: undefined,
		visitingAddress: {
			streetName: undefined,
			postalCode: undefined,
			houseNumber: undefined,
			country: undefined,
			city: undefined,
			addition: undefined,
		},
		billingAddress: {
			streetName: undefined,
			postalCode: undefined,
			houseNumber: undefined,
			country: undefined,
			city: undefined,
			addition: undefined,
		},
	};

	const [state, mergeState] = useMergeState(initialState);
	const [isValidated, setValidated] = useState(false);
	const [isDisabled, setDisabled] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);
	const [canEditCompany, setCanEditCompany] = useState(false);
	const [showConfirmArchive, confirmArchive] = useState(false);
	const [isArchived, setArchived] = useState(false);
	const [dationInstances, setDationInstances] = useState([]);

	const [showInviteModal, setShowInviteModal] = useState(false);
	const [showDeleteModal, setDeleteModal] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);
	const [sharingMode, setSharingMode] = useState('private');
	const [showLinkModal, setShowLinkModal] = useState(false);
	const [showUnlinkModal, setShowUnlinkModal] = useState(false);
	const [selectedIdentifier, setSelectedIdentifier] = useState(null);
	const userName = getDisplayName(selectedUser || {});

	const [{ data }, refetchCompany] = useApi.get(ROUTES.companyPage(companyId), {}, { cachePolicy: 'no-cache' });
	const [{ data: publicCompanies = [] }] = useApi.get(
		ROUTES.COMPANIES,
		{ archived: false },
		{ cachePolicy: 'no-cache' },
	);
	const [{ data: updatedCompanyData }, submitCompany] = useApi.put(ROUTES.companyPage(companyId));
	const [{ data: exportedStudents }, exportEmployees] = useApi.get(
		ROUTES.exportCompanyEmployees(companyId),
		{},
		{ lazy: true },
	);
	const [{ data: contactPersons }, fetchContactPersons] = useApi.get(
		ROUTES.getCompanyContactPersons(companyId),
		{},
		{ cachePolicy: 'no-cache' },
	);
	const [{ data: dationInstanceData }] = useApi.get(
		`${dationInstanceEndpoint()}`,
		{ archived: false },
		{ cachePolicy: 'no-cache' },
	);
	const [, deleteIdentifier] = useApi.delete(
		selectedIdentifier?.id
			? ROUTES.deleteCompanyDationIdentifier(selectedIdentifier.id)
			: null,
	);

	const handleUnlink = () =>
		deleteIdentifier()
			.then(() => {
				toast.addToast(t('company.unlinked'));
				refetchCompany();
			})
			.catch((e) => toast.addToast(parseErrorMessage(e), 'error'))
			.finally(() => {
				setShowUnlinkModal(false);
				navigate(ROUTES.COMPANIES);
			});

	const [shouldDownloadExport, setShouldDownloadExport] = useState(false);

	useEffect(() => {
		setDationInstances(dationInstanceData);
	}, [dationInstanceData]);

	useEffect(() => {
		if (updatedCompanyData && updatedCompanyData.archived !== 'undefined') {
			setArchived(updatedCompanyData.archived);
		}
	}, [updatedCompanyData]);

	useEffect(() => {
		if (shouldDownloadExport && exportedStudents !== null) {
			const download = document.createElement('a');
			download.href = `data:application/xlsx;base64,${exportedStudents.content}`;
			download.download = `${exportTitle.current}.xlsx`;
			download.click();
			setShouldDownloadExport(false);
		}
	}, [exportedStudents]);

	const handleExport = (params) => {
		exportTitle.current = params?.exportAll
			? t('company.export_all_name', { currentDate: moment().format('L'), companyName: state.name })
			: t('company.partial_export', {
					currentDate: moment().format('L'),
					companyName: state.name,
					lastExportDate: moment(state.lastExportDate).format('L'),
				});
		setShouldDownloadExport(true);
		exportEmployees(params);
	};

	const submit = (event) => {
		event.preventDefault();
		event.stopPropagation();
		setErrorMessage(null);
		setDisabled(true);

		const form = event.currentTarget;

		if (form.checkValidity() !== false) {
			submitCompany(state)
				.then(() => {
					toast.addToast(t('company.updated'));
				})
				.catch((e) => {
					setErrorMessage(Array.isArray(e) ? e.join('. ') : e);
				})
				.finally(() => {
					setDisabled(false);
				});
		} else {
			setDisabled(false);
		}

		setValidated(true);
	};

	const selectOptions = useMemo(() => {
		if (publicCompanies?.length > 0) {
			// Exclude own company from list
			return formatForOptions(
				publicCompanies.filter((company) => company.id !== parseInt(companyId, 10)),
			);
		}
		return [];
	}, [publicCompanies]);

	useEffect(() => {
		if (data) {
			setArchived(data.archived);
			const editable = data.dationIdentifiers.some(
				(identifier) => identifier.dationInstance.id === profile.dationInstance.id,
			);
			setCanEditCompany(editable);
			mergeState({ ...data, sharedCompanies: formatForOptions(data.sharedCompanies) });

			if (!data.private) {
				setSharingMode('public');
			} else if (data.dationIdentifiers.length > 1) {
				setSharingMode('educators');
			} else {
				setSharingMode('private');
			}
		}
	}, [data, mergeState, profile]);

	const handleChange = (event) => {
		const { target } = event;
		const value = target.type === 'checkbox' ? target.checked : target.value;

		mergeState({ [event.target.name]: value });
	};

	const handleSharingModeChange = (mode) => {
		setSharingMode(mode);
		mergeState({ private: mode !== 'public' });
	};

	const handlePrimaryEducatorChange = (event) => {
		mergeState({
			primaryDationInstance: event.target.value ? {
				id: parseInt(event.target.value, 10),
			} : null,
		});
	};

	const handleVisitingAddressChange = (event) => {
		mergeState({
			visitingAddress: {
				...state.visitingAddress,
				[event.target.name]: event.target.value,
			},
		});
	};

	const handleBillingAddressChange = (event) => {
		mergeState({
			billingAddress: {
				...state.billingAddress,
				[event.target.name]: event.target.value,
			},
		});
	};

	const toggleArchive = () => {
		const companyData = {
			companyRegistrationNumber: state.companyRegistrationNumber,
			archived: !isArchived,
		};
		submitCompany(companyData)
			.then(() => {
				fetchContactPersons();
				confirmArchive(false);
				toast.addToast(isArchived ? t('company.company_activated') : t('company.company_archived'));
			})
			.catch((e) => {
				confirmArchive(false);
				const error = `${isArchived ? t('company.activate_company_error') : t('company.archive_company_error')} ${parseErrorMessage(e)}`;
				toast.addToast(error, 'error');
			});
	};

	const dropdownOptions = useMemo(
		() =>
			[
				{
					id: 1,
					text: t('company.export_all_employees'),
					icon: 'export',
					handleClick: () => handleExport({ exportAll: true }),
				},
				{
					id: 2,
					text: t('company.export_employees_from_last_export'),
					icon: 'export',
					handleClick: () => handleExport(),
				},
				canEditCompany
					? {
							id: 3,
							text: isArchived ? t('company.activate') : t('company.archive'),
							icon: isArchived ? 'undo' : 'archive',
							handleClick: () => confirmArchive(true),
						}
					: null,
			].filter((item) => item),
		[isArchived, handleExport, canEditCompany],
	);

	return (
		<>
			<InfoBar
				backTitle={t('company.companies')}
				badgeTitle={t('company.archived')}
				showBadge={isArchived}
			/>
			<div className="row justify-content-center">
				<div className="col-12 col-xl-11">
					<Card className="mb-3">
						<Card.Body>
							<Form
								className="row company-form"
								noValidate
								validated={isValidated}
								onSubmit={submit}>
								<div className="col-6 col-xl-6">
									<div className="d-flex justify-content-between">
										<h5>{t('company.general_data')}</h5>
										{canEditCompany && (
											<Button
												variant="primary"
												size="sm"
												disabled={isDisabled}
												type="submit"
												className="ms-1">
												{t('company.update')}
											</Button>
										)}
									</div>
									<ErrorMessage message={errorMessage} />
									<Row className="mb-3">
										<FormCol
											label={t('company.name')}
											name="name"
											value={state.name}
											onChange={handleChange}
										/>
										<FormCol
											label={t('company.email')}
											type="email"
											name="emailAddress"
											value={state.emailAddress}
											onChange={handleChange}
											feedback={t('portal.email_not_valid')}
										/>
									</Row>
									<Row className="mb-3">
										<FormCol
											label={t('company.vatNumber')}
											name="vatNumber"
											value={state.vatNumber}
											onChange={handleChange}
										/>
										<FormCol
											label={t('company.phone')}
											name="phoneNumber"
											value={state.phoneNumber}
											onChange={handleChange}
										/>
									</Row>
									<Row className="mb-3">
										<FormCol
											label={t('company.website')}
											name="website"
											value={state.website}
											onChange={handleChange}
										/>
										<FormCol
											id="companyRegistrationNumber"
											label={t('company.company_registration_number')}
											name="companyRegistrationNumber"
											value={state.companyRegistrationNumber}
											onChange={handleChange}
											maxLength="8"
											pattern="\d*"
											minLength="8"
											type="text"
											feedback={t('company.company_registration_number_length')}
										/>
									</Row>
									<Row className="mb-3">
										<Col xs={6}>
											<SmallFormLabel label={t('company.head_trainer')} />
											<Form.Select
												value={state?.primaryDationInstance?.id ? parseInt(state.primaryDationInstance.id, 10) : undefined}
												onChange={handlePrimaryEducatorChange}>
												<option value="">{t('company.default_primary_educator_name')}</option>
												{dationInstances?.map(({ id, name }) => (
													<option value={id} key={id}>
														{name}
													</option>
												))}
											</Form.Select>
										</Col>
										<Col xs={6}>
											<SmallFormLabel label={t('company.share_reservations')} />
											<MultiSelect
												value={state.sharedCompanies}
												options={selectOptions}
												onChange={(value) => mergeState({ sharedCompanies: value })}
											/>
										</Col>
									</Row>
									<Row className="mb-3">
										<Col xs={6}>
											<div><SmallFormLabel label={t('company.type')} /></div>
											<div>
												<Form.Check inline type="radio">
													<Form.Check.Input
														type="radio"
														name="sharingMode"
														id="sharingMode-private"
														checked={sharingMode === 'private'}
														disabled={data?.dationIdentifiers?.length > 1}
														onChange={() => handleSharingModeChange('private')}
													/>
													<Form.Check.Label htmlFor="sharingMode-private">
														{t('company.private')}
													</Form.Check.Label>
												</Form.Check>
												<Form.Check inline type="radio">
													<Form.Check.Input
														type="radio"
														name="sharingMode"
														id="sharingMode-educators"
														checked={sharingMode === 'educators'}
														onChange={() => handleSharingModeChange('educators')}
													/>
													<Form.Check.Label htmlFor="sharingMode-educators">
														{t('company.educators')}
													</Form.Check.Label>
												</Form.Check>
												<Form.Check inline type="radio">
													<Form.Check.Input
														type="radio"
														name="sharingMode"
														id="sharingMode-public"
														checked={sharingMode === 'public'}
														onChange={() => handleSharingModeChange('public')}
													/>
													<Form.Check.Label htmlFor="sharingMode-public">
														{t('company.public')}
													</Form.Check.Label>
												</Form.Check>
											</div>
										</Col>
									</Row>
									{sharingMode === 'educators' && (
										<Row className="mb-3">
											<Col xs={12}>
												<div className="d-flex justify-content-between align-items-center mb-1">
													<SmallFormLabel label={t('company.linked_driving_schools')} />
													{canEditCompany && (
														<Button
															size="sm"
															variant="outline-secondary"
															onClick={() => setShowLinkModal(true)}>
															{t('company.link_driving_school')}
														</Button>
													)}
												</div>
												<ul className="list-group">
													{data?.dationIdentifiers?.map((identifier) => {
														const isOwn =
															identifier.dationInstance.id === profile.dationInstance.id;
														const canUnlink = isOwn && data.dationIdentifiers.length > 1;
														return (
															<li
																key={identifier.id}
																className="list-group-item d-flex justify-content-between align-items-center">
																{identifier.dationInstance.name}
																{canUnlink && (
																	<Button
																		size="sm"
																		variant="outline-danger"
																		onClick={() => {
																			setSelectedIdentifier(identifier);
																			setShowUnlinkModal(true);
																		}}>
																		{t('company.unlink_driving_school')}
																	</Button>
																)}
															</li>
														);
													})}
												</ul>
											</Col>
										</Row>
									)}
									<Row className="mb-3">
										<FormCol
											label={t('company.department')}
											name="department"
											value={state.department}
											onChange={handleChange}
										/>
									</Row>

									<Row className="mb-3">
										<Col xs={12}>
											<SmallFormLabel label={t('company.notes')} />
											<Form.Control
												as="textarea"
												rows={3}
												name="notes"
												value={state.notes ?? ''}
												onChange={handleChange}
											/>
										</Col>
									</Row>
									<Row className="mb-3">
										<Col xs={6}>
											<Form.Check type="checkbox">
												<Form.Check.Input
													type="checkbox"
													name="soob"
													checked={state.soob}
													onChange={handleChange}
												/>
												<Form.Check.Label
													onClick={() =>
														mergeState({
															soob: !state.soob,
														})
													}>
													{t('company.soob')}
												</Form.Check.Label>
											</Form.Check>
										</Col>
									</Row>
								</div>
								<div className="col-6 col-xl-6">
									<Address
										defaultAddress={state.visitingAddress || {}}
										header={t('company.visiting_address')}
										onChange={handleVisitingAddressChange}>
										<DropdownButton options={dropdownOptions} minWidth="20vw" placement="left" />
									</Address>
									<div className="dropdown-divider" />
									<Address
										defaultAddress={state.billingAddress || {}}
										header={t('company.billing_address')}
										onChange={handleBillingAddressChange}
									/>
								</div>
							</Form>
							<ConfirmModal
								show={showConfirmArchive}
								setShow={confirmArchive}
								warning={!isArchived ? t('company.archive_company_warning') : null}
								title={isArchived ? t('company.activate') : t('company.archive')}
								message={
									isArchived
										? t('company.activate_company_confirm')
										: t('company.archive_company_confirm')
								}
								handleSubmit={toggleArchive}
							/>
							<LinkDrivingSchoolModal
								show={showLinkModal}
								setShow={setShowLinkModal}
								companyId={companyId}
								dationIdentifiers={data?.dationIdentifiers ?? []}
								dationInstances={dationInstances ?? []}
								handleSubmit={refetchCompany}
							/>
							<ConfirmModal
								show={showUnlinkModal}
								setShow={setShowUnlinkModal}
								title={t('company.unlink_driving_school')}
								message={t('company.unlink_confirm', {
									name: selectedIdentifier?.dationInstance?.name,
								})}
								handleSubmit={handleUnlink}
							/>
						</Card.Body>
					</Card>
				</div>
				<div className="col-12 col-xl-11 mx-auto py-4">
					<Card>
						<Card.Body>
							<div className="justify-content-between d-flex">
								<h5>{t('add_employee.contact_persons')}</h5>
								<button
									id="add-company-contact-person"
									type="button"
									className="btn btn-outline pt-0"
									onClick={() =>
										isArchived
											? toast.addToast(t('users.add_not_allowed'), 'error')
											: navigate(EDIT_USER, {
													state: {
														userType: 'contact_person',
														original: {},
														company: data,
													},
												})
									}>
									<span className="glyphicons glyphicons-user-plus font-size-large text-secondary" />
								</button>
							</div>
							<CompanyContactPersonOverview
								users={contactPersons || []}
								handleDelete={(original) => {
									setSelectedUser(original);
									setDeleteModal(true);
								}}
								handleRowClick={(original) => {
									navigate(EDIT_USER, {
										state: {
											userType: 'contact_person',
											original,
											company: data,
										},
									});
								}}
								handleInvite={(original) => {
									setSelectedUser(original);
									setShowInviteModal(true);
								}}
							/>
							<InviteUserModal
								show={showInviteModal}
								setShow={setShowInviteModal}
								endpoint={
									selectedUser?.companyContactPersonUser
										? getSendInviteUrl(selectedUser?.id)
										: inviteContactPersonToPortal(selectedUser?.id)
								}
								handleSubmit={fetchContactPersons}
								title={t('users.send_invite')}
								message={t('users.confirm_invite', { emailAddress: selectedUser?.emailAddress })}
								successMessage={t('users.invite_send', {
									userType: t(`users.type.contact_person`),
									name: userName,
								})}
							/>
							<DeleteUserModal
								deleteEndpoint={deleteCompanyContactPerson(selectedUser?.id, data?.id)}
								show={showDeleteModal}
								setShow={(show) => {
									setSelectedUser({});
									setDeleteModal(show);
								}}
								title={t('users.delete')}
								message={t('users.confirm_delete', {
									userType: t(`users.type.contact_person`),
									name: userName,
								})}
								handleSubmit={fetchContactPersons}
								successMessage={t('users.deleted', {
									userType: t(`users.type.contact_person`),
									name: userName,
								})}
							/>
						</Card.Body>
					</Card>
				</div>
			</div>
		</>
	);
}

export default CompanyDetailPage;
